import React from "react";
import { Chip } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import HelpOutline from "@mui/icons-material/HelpOutline";
import HourglassBottom from "@mui/icons-material/HourglassBottom";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LinkOffOutlined from "@mui/icons-material/LinkOffOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import SyncOutlined from "@mui/icons-material/SyncOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

import { IconKey, StatusMeta, Tone } from "./statusLabels";

/** The icon component for each IconKey used by status labels. */
export const STATUS_ICONS: Record<IconKey, SvgIconComponent> = {
  schedule: ScheduleOutlined,
  send: SendOutlined,
  check: CheckCircleOutline,
  error: ErrorIcon,
  pause: PauseCircleOutline,
  hourglass: HourglassBottom,
  warning: WarningAmberOutlined,
  close: CloseOutlined,
  help: HelpOutline,
  sync: SyncOutlined,
  block: BlockOutlined,
  link: LinkOutlined,
  link_off: LinkOffOutlined,
  info: InfoOutlined,
  review: RateReviewOutlined,
};

export type StatusChipSize = "small" | "medium";

export type StatusChipProps =
  | {
      /** Label, tone and icon from a statusLabels helper, e.g. billStatusMeta(bill.status). */
      meta: StatusMeta;
      size?: StatusChipSize;
      tone?: never;
      label?: never;
      icon?: never;
    }
  | {
      meta?: never;
      tone: Tone;
      label: React.ReactNode;
      /** Optional icon; omit for a text-only chip. */
      icon?: IconKey;
      size?: StatusChipSize;
    };

/**
 * Bordered pill showing one status in its tone colour (theme.palette.status), with an
 * icon. Works in light and dark mode. Use `meta` from statusLabels, or `tone` + `label`.
 *
 *   <StatusChip meta={billStatusMeta(bill.status)} />
 *   <StatusChip tone="info" label="Pending review" icon="review" />
 */
export function StatusChip(props: StatusChipProps) {
  const { size = "medium" } = props;
  const tone: Tone = props.meta ? props.meta.tone : props.tone;
  const label = props.meta ? props.meta.label : props.label;
  const iconKey = props.meta ? props.meta.icon : props.icon;
  const Icon = iconKey ? STATUS_ICONS[iconKey] : undefined;
  const small = size === "small";

  return (
    <Chip
      variant="outlined"
      size={small ? "small" : "medium"}
      icon={Icon ? <Icon /> : undefined}
      label={label}
      sx={(theme) => {
        const s = theme.palette.status[tone];
        return {
          height: small ? 20 : 24,
          fontSize: small ? "0.6875rem" : "0.75rem",
          fontWeight: 500,
          color: s.fg,
          backgroundColor: s.bg,
          borderColor: s.line,
          maxWidth: "100%",
          "& .MuiChip-icon": {
            color: "inherit",
            fontSize: small ? 13 : 15,
            marginLeft: small ? "5px" : "6px",
            marginRight: "-4px",
          },
          "& .MuiChip-label": {
            paddingLeft: small ? "7px" : "8px",
            paddingRight: small ? "8px" : "9px",
          },
        };
      }}
    />
  );
}

export default StatusChip;
